import { useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import Typography from '@mui/material/Typography'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faFileLines,
  faFilePdf,
  faPlus,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons'
import {
  EllipsisText,
  presignChatDocumentAPI,
  updateAppAlert,
  uploadFileToPresignedUrl,
} from '@labrav/common'
import {
  attachFundingDocuments,
  FundingRunAttachment,
} from '../../api/fundingApi'
import { SectionCard } from './SectionCard'
import { useStyles } from './SupportingDocuments.styles'

const ACCEPTED_FILE_TYPES = 'application/pdf,.pdf'

const DEFAULT_BUSINESS_USE_CASE = 'Other'

const BUSINESS_USE_CASES = [
  'Statement of Work',
  'Project Plan/Checklist',
  'AWS Pricing Calculator',
  'Proof of Cost',
  'Innovation Plan',
  'Marketing Plan',
  'Assess Analysis Report',
  'Well Architected Review Report',
  'Success Criteria Report',
  'Customer Sign Off Template',
  'Partner Sign Off Template',
  'Customer Satisfaction Survey (CSAT)',
  'Migration Capability Review',
  'Migration Completion report',
  'Migration Assessment Scope Checklist',
  'Migration Assessment Report',
  'Managed Services Agreement',
  'Partner Scope Checklist',
  'Other',
]

interface SupportingDocumentsProps {
  serviceUrl: string
  accountId: string
  program: string
  workflowId: string
  displayStatus: string | null | undefined
  attachments: FundingRunAttachment[] | null | undefined
  onUploaded: () => void
}

export const SupportingDocuments = ({
  serviceUrl,
  accountId,
  program,
  workflowId,
  displayStatus,
  attachments,
  onUploaded,
}: SupportingDocumentsProps) => {
  const { classes, cx } = useStyles()
  const dispatch = useDispatch()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [dialogOpen, setDialogOpen] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [businessUseCase, setBusinessUseCase] = useState(
    DEFAULT_BUSINESS_USE_CASE
  )
  const [isUploading, setIsUploading] = useState(false)

  const documents = attachments ?? []
  const canUpload = displayStatus === 'DRAFT' || displayStatus === 'AWS_DRAFT'

  const closeDialog = () => {
    setDialogOpen(false)
    setFile(null)
    setBusinessUseCase(DEFAULT_BUSINESS_USE_CASE)
  }

  const onUpload = async () => {
    if (!file) return
    setIsUploading(true)
    try {
      const { data } = await presignChatDocumentAPI(accountId, {
        filename: file.name,
        doc_type: 'other',
        content_type: 'application/pdf',
      })
      await uploadFileToPresignedUrl(data.upload_url, file, data.content_type)
      await attachFundingDocuments(serviceUrl, accountId, program, workflowId, [
        {
          s3_uri: data.s3_uri,
          filename: file.name,
          doc_type: 'other',
          business_use_case: businessUseCase,
        },
      ])
      closeDialog()
      onUploaded()
    } catch {
      dispatch(
        updateAppAlert({
          message: 'Unable to upload document. Please try again.',
          messageType: 'ERROR',
          autoClose: true,
        })
      )
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <SectionCard
      icon={faFileLines}
      title="Supporting documents"
      collapsible
      testId="funding-detail-documents"
      headerRight={
        canUpload && (
          <button
            type="button"
            className={classes.uploadButton}
            onClick={() => setDialogOpen(true)}
          >
            <FontAwesomeIcon icon={faPlus} />
            <Typography component="span" className={classes.uploadLabel}>
              Upload document
            </Typography>
          </button>
        )
      }
    >
      {documents.length === 0 ? (
        <Typography component="span" className={classes.empty}>
          No documents attached yet.
        </Typography>
      ) : (
        <div className={classes.list}>
          {documents.map(doc => {
            const name = doc.filename || doc.file_path
            const inner = (
              <>
                <FontAwesomeIcon icon={faFilePdf} />
                <EllipsisText text={name} className={classes.filename} />
                {doc.business_use_case && (
                  <Typography component="span" className={classes.useCase}>
                    {doc.business_use_case}
                  </Typography>
                )}
              </>
            )

            return doc.cloudfront_url ? (
              <a
                key={doc.s3_uri}
                className={classes.docPill}
                href={doc.cloudfront_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {inner}
              </a>
            ) : (
              <span key={doc.s3_uri} className={classes.docPill}>
                {inner}
              </span>
            )
          })}
        </div>
      )}

      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        classes={{ paper: classes.paper }}
        data-testid="funding-upload-document-dialog"
      >
        <div className={classes.dialogHeader}>
          <Typography component="h2" className={classes.dialogTitle}>
            Upload document
          </Typography>
          <button
            type="button"
            className={classes.close}
            aria-label="Close"
            onClick={closeDialog}
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        <div className={classes.dialogBody}>
          <input
            ref={fileInputRef}
            type="file"
            hidden
            accept={ACCEPTED_FILE_TYPES}
            onChange={event => {
              setFile(event.target.files?.[0] ?? null)
              event.target.value = ''
            }}
          />
          <button
            type="button"
            className={classes.filePicker}
            onClick={() => fileInputRef.current?.click()}
          >
            <FontAwesomeIcon icon={faFilePdf} />
            <Typography component="span" className={classes.filePickerLabel}>
              {file ? file.name : 'Choose a PDF'}
            </Typography>
          </button>
          <Typography component="span" className={classes.helper}>
            PDF only. Files are sent to AWS with this request.
          </Typography>

          <label className={classes.field}>
            <Typography component="span" className={classes.fieldLabel}>
              Business use case
            </Typography>
            <select
              className={classes.select}
              value={businessUseCase}
              onChange={event => setBusinessUseCase(event.target.value)}
            >
              {BUSINESS_USE_CASES.map(useCase => (
                <option key={useCase} value={useCase}>
                  {useCase}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className={classes.dialogFooter}>
          <button
            type="button"
            className={cx(classes.button, classes.cancelButton)}
            onClick={closeDialog}
          >
            Cancel
          </button>
          <button
            type="button"
            className={cx(classes.button, classes.confirmButton)}
            disabled={!file || isUploading}
            onClick={onUpload}
          >
            {isUploading && (
              <CircularProgress
                size={16}
                thickness={5}
                className={classes.spinner}
              />
            )}
            Upload
          </button>
        </div>
      </Dialog>
    </SectionCard>
  )
}
